import { Routes } from '@angular/router';
import { authGuard, guestGuard } from './guards/auth.guard';
import { adminGuard } from './guards/admin.guard';
import { unsavedChangesGuard } from './guards/unsaved-changes.guard';

export const appRoutes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/landing/landing.component').then((m) => m.LandingComponent),
  },
  {
    path: 'pricing',
    loadComponent: () =>
      import('./pages/pricing/pricing.component').then((m) => m.PricingComponent),
  },
  {
    path: 'features',
    loadComponent: () =>
      import('./pages/features/features.component').then((m) => m.FeaturesComponent),
  },
  {
    path: 'multilingual-ai-chatbot',
    loadComponent: () =>
      import('./pages/multilingual/multilingual.component').then((m) => m.MultilingualComponent),
  },
  {
    path: 'solutions',
    loadComponent: () =>
      import('./pages/solutions/solutions-index.component').then((m) => m.SolutionsIndexComponent),
  },
  {
    path: 'solutions/:industry',
    loadComponent: () =>
      import('./pages/solutions/solution-detail.component').then((m) => m.SolutionDetailComponent),
  },
  {
    path: 'integrations',
    loadComponent: () =>
      import('./pages/integrations/integrations-index.component').then(
        (m) => m.IntegrationsIndexComponent
      ),
  },
  {
    path: 'integrations/:platform',
    loadComponent: () =>
      import('./pages/integrations/integration-detail.component').then(
        (m) => m.IntegrationDetailComponent
      ),
  },
  {
    path: 'alternatives',
    loadComponent: () =>
      import('./pages/alternatives/alternatives-index.component').then(
        (m) => m.AlternativesIndexComponent
      ),
  },
  {
    path: 'alternatives/:competitor',
    loadComponent: () =>
      import('./pages/alternatives/alternative-detail.component').then(
        (m) => m.AlternativeDetailComponent
      ),
  },
  {
    path: 'blog',
    loadComponent: () =>
      import('./pages/blog/blog-index.component').then((m) => m.BlogIndexComponent),
  },
  {
    path: 'blog/category/:category',
    loadComponent: () =>
      import('./pages/blog/blog-category.component').then((m) => m.BlogCategoryComponent),
  },
  {
    path: 'blog/:slug',
    loadComponent: () =>
      import('./pages/blog/blog-post.component').then((m) => m.BlogPostComponent),
  },
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'register',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/auth/register/register.component').then((m) => m.RegisterComponent),
  },
  {
    // No guard: a signed-in browser must still be able to request/complete
    // a reset — guestGuard used to bounce it straight to /dashboard, so the
    // emailed reset link (and the superadmin "set your password" invite,
    // which also targets this route) silently did nothing if the recipient
    // happened to have an active session on that machine.
    path: 'forgot-password',
    loadComponent: () =>
      import('./pages/auth/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent
      ),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./pages/auth/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent
      ),
  },
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./pages/auth/verify-email/verify-email.component').then(
        (m) => m.VerifyEmailComponent
      ),
  },
  {
    // No guard: must work for a brand-new invitee (logged out) and for an
    // existing user accepting an invite while already logged in.
    path: 'accept-invite',
    loadComponent: () =>
      import('./pages/auth/accept-invite/accept-invite.component').then(
        (m) => m.AcceptInviteComponent
      ),
  },
  {
    // Outreach sales demo (see the ProspectDemo model). Public and
    // unauthenticated — the recipient of a cold email has no account. Short
    // path because it goes in emails and gets read aloud on calls.
    path: 'd/:token',
    loadComponent: () =>
      import('./pages/prospect-demo/prospect-demo.component').then((m) => m.ProspectDemoComponent),
  },
  {
    path: 'privacy',
    loadComponent: () =>
      import('./pages/legal/privacy.component').then((m) => m.PrivacyComponent),
  },
  {
    path: 'terms',
    loadComponent: () =>
      import('./pages/legal/terms.component').then((m) => m.TermsComponent),
  },
  {
    path: 'dashboard',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/dashboard/dashboard-layout.component').then(
        (m) => m.DashboardLayoutComponent
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/dashboard/home/dashboard-home.component').then(
            (m) => m.DashboardHomeComponent
          ),
      },
      {
        path: 'conversations',
        loadComponent: () =>
          import('./pages/dashboard/conversations/conversations.component').then(
            (m) => m.ConversationsComponent
          ),
      },
      {
        path: 'content',
        loadComponent: () =>
          import('./pages/dashboard/content/content.component').then(
            (m) => m.ContentComponent
          ),
      },
      {
        // Path-less: groups Behavior + Widget under one shared live-preview
        // shell without changing their URLs (still /dashboard/behavior and
        // /dashboard/widget).
        path: '',
        loadComponent: () =>
          import('./pages/dashboard/chatbot-setup/chatbot-setup-layout.component').then(
            (m) => m.ChatbotSetupLayoutComponent
          ),
        children: [
          {
            path: 'behavior',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./pages/dashboard/chatbot-behavior/chatbot-behavior.component').then(
                (m) => m.ChatbotBehaviorComponent
              ),
          },
          {
            path: 'widget',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./pages/dashboard/widget-settings/widget-settings.component').then(
                (m) => m.WidgetSettingsComponent
              ),
          },
        ],
      },
      {
        path: 'analytics',
        loadComponent: () =>
          import('./pages/dashboard/analytics/analytics.component').then(
            (m) => m.AnalyticsComponent
          ),
      },
      {
        path: 'leads',
        loadComponent: () =>
          import('./pages/dashboard/leads/leads.component').then(
            (m) => m.LeadsComponent
          ),
      },
      {
        path: 'onboarding',
        loadComponent: () =>
          import('./pages/dashboard/onboarding/onboarding.component').then(
            (m) => m.OnboardingComponent
          ),
      },
      {
        path: 'billing',
        loadComponent: () =>
          import('./pages/dashboard/billing/billing.component').then(
            (m) => m.BillingComponent
          ),
      },
      {
        path: 'settings',
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./pages/dashboard/settings/settings.component').then(
            (m) => m.SettingsComponent
          ),
      },
      {
        path: 'team',
        loadComponent: () =>
          import('./pages/dashboard/team/team.component').then(
            (m) => m.TeamComponent
          ),
      },
      {
        path: 'support',
        loadComponent: () =>
          import('./pages/dashboard/support/support.component').then(
            (m) => m.SupportComponent
          ),
      },
    ],
  },
  {
    path: 'admin',
    canActivate: [authGuard, adminGuard],
    loadComponent: () =>
      import('./pages/admin/admin-layout.component').then((m) => m.AdminLayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/admin/overview/admin-overview.component').then(
            (m) => m.AdminOverviewComponent
          ),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./pages/admin/users/admin-users.component').then(
            (m) => m.AdminUsersComponent
          ),
      },
      {
        path: 'users/:id',
        loadComponent: () =>
          import('./pages/admin/users/admin-user-detail.component').then(
            (m) => m.AdminUserDetailComponent
          ),
      },
      {
        path: 'sources',
        loadComponent: () =>
          import('./pages/admin/sources/admin-sources.component').then(
            (m) => m.AdminSourcesComponent
          ),
      },
      {
        path: 'storage',
        loadComponent: () =>
          import('./pages/admin/storage/admin-storage.component').then(
            (m) => m.AdminStorageComponent
          ),
      },
      {
        path: 'system',
        loadComponent: () =>
          import('./pages/admin/system/admin-system.component').then(
            (m) => m.AdminSystemComponent
          ),
      },
      {
        path: 'audit-log',
        loadComponent: () =>
          import('./pages/admin/audit-log/admin-audit-log.component').then(
            (m) => m.AdminAuditLogComponent
          ),
      },
      {
        path: 'tickets',
        loadComponent: () =>
          import('./pages/admin/tickets/admin-tickets.component').then(
            (m) => m.AdminTicketsComponent
          ),
      },
      {
        path: 'outreach',
        loadComponent: () =>
          import('./pages/admin/outreach/admin-outreach.component').then(
            (m) => m.AdminOutreachComponent
          ),
      },
      {
        path: 'churn',
        loadComponent: () =>
          import('./pages/admin/churn/admin-churn.component').then(
            (m) => m.AdminChurnComponent
          ),
      },
    ],
  },
  {
    path: '**',
    loadComponent: () =>
      import('./pages/not-found/not-found.component').then((m) => m.NotFoundComponent),
  },
];
